/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {AriaLabelingProps, DOMProps} from '@react-types/shared';
import {CalendarAria, useCalendarBase} from './useCalendarBase';
import {
  CalendarProps,
  CalendarSelectionMode,
  CalendarState,
  DateValue
} from 'react-stately/useCalendarState';

export interface AriaCalendarProps<T extends DateValue, M extends CalendarSelectionMode = 'single'>
  extends CalendarProps<T, M>, DOMProps, AriaLabelingProps {}

/**
 * Provides the behavior and accessibility implementation for a calendar component.
 * A calendar displays one or more date grids and allows users to select a single date.
 */
export function useCalendar<T extends DateValue, M extends CalendarSelectionMode = 'single'>(
  props: AriaCalendarProps<T, M>,
  state: CalendarState<M>
): CalendarAria {
  return useCalendarBase(props, state);
}
